import * as API from '../../api/orgUsersAPI'
import {
  MutationFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useAuth0 } from '@/utils/auth/auth0'

export function useOrgUsers(token: string | null, orgUuid: string) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery({
    queryKey: ['orgUsers', orgUuid],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.getOrgUsers(orgUuid, freshToken)
    },
    enabled: !!token && !!orgUuid,
    refetchOnWindowFocus: true,
  })

  return {
    ...query,
    data: Array.isArray(query.data) ? query.data : undefined,
    deleteOrgUser: useDeleteOrgUserMutation(orgUuid),
    updateOrgUser: useUpdateOrgUserMutation(orgUuid),
  }
}

export function useOrgUserMutation<TData, TVariables>(
  apiClientFn: MutationFunction<TData, TVariables>,
  orgUuid: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClientFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgUsers', orgUuid] })
    },
  })
}

export function useDeleteOrgUserMutation(orgUuid: string) {
  return useOrgUserMutation(API.deleteOrgUser, orgUuid)
}
export function useUpdateOrgUserMutation(orgUuid: string) {
  return useOrgUserMutation(API.updateOrgUser, orgUuid)
}

function useInvitationMutation<TData, TVariables>(
  apiClientFn: MutationFunction<TData, TVariables>,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClientFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
    },
  })
}

export function useSendInvitationMutation() {
  return useInvitationMutation(API.sendOrgUserInvitation)
}

export function useUpdateInvitation() {
  return useInvitationMutation(API.updateInvitationStatus)
}

export function useDeleteInvitation() {
  return useInvitationMutation(API.deleteInvitation)
}

export function useInvitations(token: string | null, orgUuid: string) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery({
    queryKey: ['invitations', orgUuid],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.getInvitations(freshToken, orgUuid)
    },
    enabled: !!token && !!orgUuid,
    refetchOnWindowFocus: true,
  })

  return {
    ...query,
    data: Array.isArray(query.data) ? query.data : undefined,
    updateInvitation: useUpdateInvitation(),
    deleteInvitation: useDeleteInvitation(),
    sendInvitation: useSendInvitationMutation(),
  }
}

export function useInvitationDetails(token: string, invitationToken: string) {
  const { getAccessTokenSilently } = useAuth0()

  return useQuery({
    queryKey: ['invitation', invitationToken],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.getInvitationDetails(invitationToken, freshToken)
    },
    enabled: !!token && !!invitationToken,
    retry: false,
    refetchOnWindowFocus: true,
  })
}

export function useMyPendingInvitations(token: string | null) {
  const { getAccessTokenSilently } = useAuth0()

  return useQuery({
    queryKey: ['myPendingInvitations'],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.getMyPendingInvitations(freshToken)
    },
    enabled: !!token,
    refetchOnWindowFocus: true,
  })
}
